import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';
import { colors, fonts, gradients, breakpoints, zIndex, shadows, radii } from './tokens';
import { Button } from './Button';
import { IconButton } from './IconButton';
import { DocumentIcon, MenuIcon, CloseIcon } from './icons';
import { ReactComponent as EngageLogo } from './assets/ibisengage-logo.svg';

// Figma session header: NEW IBISengage teal gradient, white Bold title on the left,
// [IBISengage logo] [See Notes] [hamburger] on the right. On phones the wordmark sits above the title.

const Bar = styled.header`
  position: sticky;
  top: 0;
  z-index: ${zIndex.header};
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 88px;
  padding: 12px 32px;
  box-sizing: border-box;
  background: ${({ variant }) => gradients[variant] || gradients.engage};
  color: #fff;
  font-family: ${fonts.family};

  @media (max-width: ${breakpoints.phone - 1}px) {
    min-height: 64px;
    padding: 10px 12px;
    gap: 8px;
  }
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const Title = styled('h1', { shouldForwardProp: (p) => p !== 'hideOnPhone' })`
  margin: 0;
  font-size: 28px;
  line-height: 36px;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: ${breakpoints.phone - 1}px) {
    font-size: 18px;
    line-height: 24px;
    /* Figma iPhone lobby shows only the wordmark */
    ${({ hideOnPhone }) => (hideOnPhone ? 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);' : '')}
  }
`;

// "IBIS" (heavy, uppercase) + the "engage™" wordmark SVG from the dashboard assets.
const Logo = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ w }) => Math.round(w * 0.02)}px;
  color: inherit;
  line-height: 1;
  b {
    font-family: ${fonts.family};
    font-weight: 800;
    font-size: ${({ w }) => Math.round(w * 0.2)}px;
    letter-spacing: -0.02em;
    transform: translateY(-${({ w }) => Math.round(w * 0.012)}px);
  }
  svg {
    width: ${({ w }) => Math.round(w * 0.64)}px;
    height: auto;
    display: block;
  }
`;

const PhoneOnly = styled.div`
  display: none;
  @media (max-width: ${breakpoints.phone - 1}px) {
    display: block;
  }
`;

const WideOnly = styled.div`
  display: contents;
  @media (max-width: ${breakpoints.phone - 1}px) {
    display: none;
  }
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  position: relative;
  @media (max-width: ${breakpoints.phone - 1}px) {
    gap: 8px;
  }
`;

export const EngageWordmark = ({ width = 112 }) => (
  <Logo w={width} aria-label='IBISengage' role='img'>
    <b aria-hidden='true'>IBIS</b>
    <EngageLogo aria-hidden='true' />
  </Logo>
);

/**
 * SessionHeader
 * @param title       screen title ("IBISengage Appointment", "Calibration Selection", ...)
 * @param showLogo    show the IBISengage wordmark on the right (lobby) - always shown above the title on phones
 * @param onSeeNotes  when set, renders the outlined "See Notes" button (clinician)
 * @param menuItems   when set, renders the hamburger and its dropdown (see SessionMenu)
 * @param right       extra nodes on the right
 */
export const SessionHeader = ({ title, showLogo = false, onSeeNotes, notesLabel = 'See Notes', menuItems, menuLabel = 'Session menu', right, variant = 'engage' }) => {
  const [open, setOpen] = useState(false);
  return (
    <Bar variant={variant} data-testid='session-header'>
      <TitleBlock>
        <PhoneOnly>
          <EngageWordmark width={showLogo ? 128 : 96} />
        </PhoneOnly>
        <Title hideOnPhone={showLogo}>{title}</Title>
      </TitleBlock>
      <Right>
        {showLogo && (
          <WideOnly>
            <EngageWordmark width={112} />
          </WideOnly>
        )}
        {right}
        {onSeeNotes && (
          <Button variant='header' size='sm' onClick={onSeeNotes} iconLeft={<DocumentIcon size={14} />} style={{ height: 36, padding: '0 12px' }}>
            {notesLabel}
          </Button>
        )}
        {menuItems && (
          <>
            <IconButton label={open ? 'Close menu' : menuLabel} variant='header' size={36} aria-haspopup='menu' aria-expanded={open} onClick={() => setOpen((o) => !o)}>
              {open ? <CloseIcon size={18} /> : <MenuIcon size={18} />}
            </IconButton>
            {open && <SessionMenu items={menuItems} onClose={() => setOpen(false)} />}
          </>
        )}
      </Right>
    </Bar>
  );
};

// --- Menu ("Menu/Extra/Mweb") ---

const Scrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.menu};
  background: ${colors.scrim};
`;

const MenuPanel = styled.ul`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: ${zIndex.menu + 1};
  min-width: 240px;
  margin: 0;
  padding: 6px 0 0;
  list-style: none;
  background: ${colors.lightTeal};
  border: 1px solid ${colors.slate[200]};
  border-radius: ${radii.dialog}px;
  box-shadow: ${shadows.menu};
  overflow: hidden;
  font-family: ${fonts.family};
`;

const MenuItemButton = styled.button`
  display: block;
  width: 100%;
  padding: 14px 16px;
  border: 0;
  background: ${({ danger }) => (danger ? colors.error[500] : 'transparent')};
  color: ${({ danger, current }) => (danger ? '#fff' : current ? colors.slate[800] : colors.slate[400])};
  font-family: inherit;
  font-size: 14px;
  line-height: 20px;
  font-weight: ${({ current, danger }) => (current || danger ? 700 : 500)};
  text-align: left;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: ${({ danger }) => (danger ? colors.error[600] : 'rgba(0, 142, 155, 0.08)')};
    color: ${({ danger }) => (danger ? '#fff' : colors.slate[800])};
  }
  &:disabled {
    cursor: default;
  }
  &:focus-visible {
    outline: 2px solid ${colors.brand[400]};
    outline-offset: -2px;
  }
`;

/**
 * SessionMenu - dropdown under the hamburger; dims the page. Items:
 *   { id, label, current?, disabled?, danger?, onSelect }
 * The current step is bold, the others grey; the danger row (End Session / Exit Session) is red.
 */
export const SessionMenu = ({ items, onClose }) => {
  const ref = useRef(null);
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    ref.current?.querySelector('button:not(:disabled)')?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <>
      <Scrim onClick={onClose} data-testid='menu-scrim' />
      <MenuPanel ref={ref} role='menu'>
        {items.map((item) => (
          <li key={item.id} role='none'>
            <MenuItemButton
              role='menuitem'
              current={item.current}
              danger={item.danger}
              disabled={item.disabled}
              aria-current={item.current ? 'step' : undefined}
              onClick={() => {
                onClose();
                item.onSelect?.();
              }}
            >
              {item.label}
            </MenuItemButton>
          </li>
        ))}
      </MenuPanel>
    </>
  );
};

export default SessionHeader;
